import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Search, Download, Send, MessageCircle, Phone, Building2, ChevronDown, Package, Instagram } from 'lucide-react';
import Seo from '../components/Seo';
import { pickMeta } from '../seoMeta';

const WHATSAPP_NUMBER = '77010982754';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Здравствуйте! Интересует сотрудничество B2B с Айнанан.')}`;
const LOGO_URL = '/logo.jpg';

export default function B2BMenu() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVariants, setSelectedVariants] = useState({});
  const [formData, setFormData] = useState({ name: '', phone: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/cities/public/products?city_code=almaty`),
        axios.get(`${API}/settings/categories`),
      ]);
      if (results[0].status === 'fulfilled') {
        const prods = (results[0].value.data.products || []).filter(p => 
          p.is_active !== false && (p.category || p.category_id)
        );
        setProducts(prods);
      }
      if (results[1].status === 'fulfilled') setCategories(results[1].value.data);
    } catch (e) { /* stale cache */ }
    finally { setLoading(false); }
  };

  const getName = (p) => p?.name_ru || p?.name || '';

  const formatPrice = (price) => new Intl.NumberFormat('ru-RU').format(Math.round(price)) + ' тг';

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (selectedCategory !== 'all' && p.category !== selectedCategory && p.category_id !== selectedCategory) return false;
      if (search) {
        return getName(p).toLowerCase().includes(search.toLowerCase());
      }
      return true;
    });
  }, [products, selectedCategory, search]);

  // Variant grouping — same logic as main Catalog
  const { displayProducts, variantMap } = useMemo(() => {
    const childrenMap = {};
    for (const p of filteredProducts) {
      if (p.parent_product_id) {
        if (!childrenMap[p.parent_product_id]) childrenMap[p.parent_product_id] = [];
        childrenMap[p.parent_product_id].push(p);
      }
    }
    for (const key of Object.keys(childrenMap)) {
      childrenMap[key].sort((a, b) => (a.variant_sort || 0) - (b.variant_sort || 0));
    }
    const display = filteredProducts.filter(p => !p.parent_product_id);
    return { displayProducts: display, variantMap: childrenMap };
  }, [filteredProducts]);

  const getActiveProduct = (product) => {
    const variants = variantMap[product.id];
    if (!variants || variants.length === 0) return product;
    const selectedId = selectedVariants[product.id];
    if (selectedId) {
      const found = variants.find(v => v.id === selectedId);
      if (found) return found;
    }
    return product;
  };

  // Group ALL products (including all variants) by category for PDF
  const pdfGrouped = useMemo(() => {
    const map = {};
    products.forEach(p => {
      if (!p.is_active) return;
      const catId = p.category || p.category_id;
      if (!catId) return; // Skip products without category (test products)
      if (!map[catId]) map[catId] = [];
      map[catId].push(p);
    });
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => getName(a).localeCompare(getName(b)));
    }
    return map;
  }, [products]);

  const getCatName = (id) => categories.find(c => c.id === id)?.name_ru || 'Другое';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) { toast.error('Укажите имя и телефон'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/b2b-inquiry`, formData);
      toast.success('Заявка отправлена! Мы свяжемся с вами.');
      setFormData({ name: '', phone: '', company: '', message: '' });
    } catch { toast.error('Ошибка. Попробуйте WhatsApp.'); }
    finally { setSubmitting(false); }
  };

  const handlePDF = () => {
    const win = window.open('', '_blank');
    const today = new Date().toLocaleDateString('ru-RU');
    const placeholderSvg = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23f3f4f6'/><path d='M30 40h40v25H30z' stroke='%23d1d5db' stroke-width='2' fill='none'/><circle cx='42' cy='52' r='4' fill='%23d1d5db'/></svg>";
    win.document.write(`<html><head><title>Айнанан — B2B Прайс-лист</title>
      <style>
        @page { size: A4; margin: 12mm 10mm; }
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1f2937; margin: 0; padding: 0; }
        .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #059669; padding-bottom: 8px; margin-bottom: 14px; }
        .brand-block { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; }
        .brand-logo { height: 50px; width: auto; border-radius: 6px; }
        .brand { font-size: 28px; font-weight: 800; color: #059669; letter-spacing: -0.5px; }
        .sub { color: #6b7280; font-size: 12px; margin-top: 2px; }
        h2 { color: #111827; font-size: 16px; font-weight: 700; margin: 18px 0 8px; padding-left: 8px; border-left: 4px solid #059669; page-break-after: avoid; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .card { display: flex; gap: 8px; border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px; background: #fff; page-break-inside: avoid; }
        .card img { width: 70px; height: 70px; object-fit: cover; border-radius: 4px; flex-shrink: 0; background: #f3f4f6; }
        .info { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: space-between; }
        .name { font-size: 11px; font-weight: 600; color: #111827; line-height: 1.2; margin-bottom: 4px; overflow-wrap: break-word; }
        .price { font-size: 13px; font-weight: 700; color: #059669; white-space: nowrap; }
        .ft { margin-top: 24px; padding-top: 10px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 11px; text-align: center; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .card { break-inside: avoid; }
        }
      </style></head><body>
      <div class="header">
        <div class="brand-block"><img src="${LOGO_URL}" alt="Ainanan" class="brand-logo"><div class="sub">B2B Прайс-лист для оптовых клиентов</div></div>
        <div class="sub">${today}</div>
      </div>`);
    Object.entries(pdfGrouped).forEach(([catId, items]) => {
      win.document.write(`<h2>${getCatName(catId)}</h2><div class="grid">`);
      items.forEach(p => {
        const img = (p.image_url || '').replace(/'/g, '&apos;') || placeholderSvg;
        const price = Math.round(p.base_price || p.retail_price || 0);
        const name = getName(p).replace(/&/g, '&amp;').replace(/</g, '&lt;');
        win.document.write(`<div class="card"><img src="${img}" onerror="this.src='${placeholderSvg}'" alt=""><div class="info"><div class="name">${name}</div><div class="price">${price} ₸</div></div></div>`);
      });
      win.document.write('</div>');
    });
    win.document.write(`<div class="ft">Айнанан — Свежая выпечка | WhatsApp: +7 701 098 2754 | ainanan.kz</div></body></html>`);
    win.document.close();
    // Wait for images to load before triggering print (onload may already have fired after document.write)
    const triggerPrint = () => setTimeout(() => { try { win.focus(); win.print(); } catch (e) {} }, 1200);
    if (win.document.readyState === 'complete') {
      triggerPrint();
    } else {
      win.onload = triggerPrint;
    }
  };

  const visibleCategories = showAllCategories ? categories : categories.slice(0, 6);

  if (loading) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="animate-pulse text-stone-400 text-lg">Загрузка каталога...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="b2b-menu-page">
      <Seo lang="ru" canonical="https://ainanan.kz/b2b-menu" title={pickMeta('b2bMenu', 'ru').title} description={pickMeta('b2bMenu', 'ru').description} />
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Ainanan" className="h-10 sm:h-12 w-auto rounded-lg" />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="outline" size="sm" onClick={handlePDF} className="gap-1.5" data-testid="pdf-download-btn">
              <Download className="w-4 h-4" /><span className="hidden sm:inline">Скачать прайс</span>
            </Button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="whatsapp-header-btn">
                <MessageCircle className="w-4 h-4" /><span className="hidden sm:inline">WhatsApp</span>
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">B2B Оптовый каталог</h1>
          <p className="text-stone-500 mt-2">Оптовые цены для партнёров</p>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Поиск по товарам..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white" data-testid="b2b-search-input" />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="b2b-category-filter">
          <button onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedCategory === 'all' ? 'bg-emerald-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'}`}>
            Все ({products.filter(p => !p.parent_product_id).length})
          </button>
          {visibleCategories.map(c => {
            const count = products.filter(p => !p.parent_product_id && (p.category === c.id || p.category_id === c.id)).length;
            if (count === 0) return null;
            return (
              <button key={c.id} onClick={() => setSelectedCategory(c.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedCategory === c.id ? 'bg-emerald-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'}`}>
                {c.name_ru} ({count})
              </button>
            );
          })}
          {categories.length > 6 && (
            <button onClick={() => setShowAllCategories(!showAllCategories)} className="px-3 py-1.5 rounded-full text-sm text-emerald-600 hover:bg-emerald-50 border border-emerald-200 flex items-center gap-1">
              {showAllCategories ? 'Свернуть' : `Ещё ${categories.length - 6}`}
              <ChevronDown className={`w-3 h-3 transition-transform ${showAllCategories ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        <p className="text-sm text-stone-500 mb-4">{displayProducts.length} товаров</p>

        {/* Product Grid — same card layout as main Catalog */}
        {displayProducts.length === 0 ? (
          <div className="text-center py-16 text-stone-400">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Товары не найдены</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4" data-testid="b2b-product-grid">
            {displayProducts.map((product) => {
              const variants = variantMap[product.id];
              const hasVariants = variants && variants.length > 0;
              const activeProduct = getActiveProduct(product);
              const isParentSelected = !selectedVariants[product.id] || selectedVariants[product.id] === product.id;
              const b2bPrice = activeProduct.base_price || activeProduct.retail_price;
              const retailPrice = activeProduct.site_price || 0;

              return (
                <div key={product.id} className="bg-white rounded-lg overflow-hidden border hover:shadow-lg transition-shadow">
                  {/* Image with hover */}
                  <div className="aspect-square bg-gray-100 relative overflow-hidden group">
                    {activeProduct.image_url ? (
                      <>
                        <img src={activeProduct.image_url} alt={getName(activeProduct)}
                          className={`w-full h-full object-cover transition-opacity duration-300 ${activeProduct.hover_image_url ? 'group-hover:opacity-0' : ''}`} loading="lazy" />
                        {activeProduct.hover_image_url && (
                          <img src={activeProduct.hover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300" loading="lazy" />
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 sm:w-12 h-8 sm:h-12 text-gray-300" />
                      </div>
                    )}
                    {retailPrice > b2bPrice && retailPrice > 0 && (
                      <Badge className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-emerald-600 text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 z-10">
                        B2B цена
                      </Badge>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-1.5 sm:p-3">
                    <h3 className="font-medium text-[11px] sm:text-sm text-gray-900 line-clamp-2 leading-tight mb-0.5">{getName(activeProduct)}</h3>
                    <p className="text-[9px] sm:text-xs text-gray-500 mb-1 sm:mb-2">
                      {activeProduct.unit_of_measure ? `за ${activeProduct.unit_of_measure}` : 'за шт'}
                    </p>

                    {/* Variant selector */}
                    {hasVariants && (
                      <div className="flex flex-wrap gap-1 mb-1.5 sm:mb-2">
                        {product.variant_label && (
                          <button onClick={() => setSelectedVariants(prev => ({...prev, [product.id]: product.id}))}
                            className={`px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-xs font-medium border transition-colors ${isParentSelected ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'}`}>
                            {product.variant_label}
                          </button>
                        )}
                        {variants.map(v => (
                          <button key={v.id} onClick={() => setSelectedVariants(prev => ({...prev, [product.id]: v.id}))}
                            className={`px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-xs font-medium border transition-colors ${selectedVariants[product.id] === v.id ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'}`}>
                            {v.variant_label || getName(v)}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Price — B2B with crossed retail */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm sm:text-lg font-bold text-emerald-700">{formatPrice(b2bPrice)}</span>
                      {retailPrice > b2bPrice && (
                        <span className="text-[10px] sm:text-xs text-stone-400 line-through">{formatPrice(retailPrice)}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Contact Section */}
        <div className="mt-16 grid md:grid-cols-2 gap-6" id="b2b-contact" data-testid="b2b-contact-section">
          <Card className="border-emerald-200 bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-emerald-600" />
                <h2 className="text-xl font-bold text-stone-900">Стать партнёром</h2>
              </div>
              <p className="text-sm text-stone-500 mb-5">Оставьте заявку и мы свяжемся с вами для обсуждения условий</p>
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="b2b-inquiry-form">
                <Input placeholder="Ваше имя *" value={formData.name} onChange={e => setFormData(f => ({...f, name: e.target.value}))} required data-testid="b2b-inquiry-name" />
                <Input placeholder="Телефон *" type="tel" value={formData.phone} onChange={e => setFormData(f => ({...f, phone: e.target.value}))} required data-testid="b2b-inquiry-phone" />
                <Input placeholder="Название компании" value={formData.company} onChange={e => setFormData(f => ({...f, company: e.target.value}))} data-testid="b2b-inquiry-company" />
                <textarea placeholder="Комментарий" value={formData.message} onChange={e => setFormData(f => ({...f, message: e.target.value}))}
                  className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="b2b-inquiry-message" />
                <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="b2b-inquiry-submit">
                  <Send className="w-4 h-4" />{submitting ? 'Отправляем...' : 'Отправить заявку'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="w-5 h-5" />
                  <h2 className="text-xl font-bold">Напишите в WhatsApp</h2>
                </div>
                <p className="text-emerald-100 text-sm mb-4">Быстрый способ связаться для обсуждения оптовых заказов</p>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-contact-btn">
                  <Button className="w-full bg-white text-emerald-700 hover:bg-emerald-50 gap-2 font-semibold">
                    <MessageCircle className="w-4 h-4" />Написать в WhatsApp
                  </Button>
                </a>
                <p className="text-emerald-200 text-xs mt-3 text-center">+7 701 098 27 54</p>
              </CardContent>
            </Card>
            <Card className="border-stone-200 bg-white">
              <CardContent className="p-6">
                <h3 className="font-semibold text-stone-900 mb-3">Контакты</h3>
                <div className="space-y-2 text-sm text-stone-600">
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-emerald-600" /><span>+7 701 098 27 54</span></div>
                  <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-600" /><span>Алматы, Казахстан</span></div>
                  <a href="https://www.instagram.com/ainanan.kz/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-pink-600 transition-colors">
                    <Instagram className="w-4 h-4 text-pink-500" /><span>@ainanan.kz</span>
                  </a>
                </div>
                <Badge className="mt-3 bg-emerald-50 text-emerald-700 border-emerald-200">Доставка по Алматы</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-stone-200 mt-16 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-sm text-stone-400">
          <p>Ainanan - Свежая выпечка каждый день</p>
          <p className="mt-1">Цены указаны оптовые (B2B). Актуальны на {new Date().toLocaleDateString('ru-RU')}</p>
        </div>
      </footer>
    </div>
  );
}
